import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CalendarPlus, Check, Mail, MailX, Phone, Save, Send, XCircle } from "lucide-react";

const STATUS_META = {
    neu: { label: "Neu", cls: "border-[#C88A45]/50 text-[#C88A45]" },
    angenommen: { label: "Eingeplant", cls: "border-[#7A9B5A]/50 text-[#7A9B5A]" },
    abgelehnt: { label: "Abgelehnt", cls: "border-red-400/50 text-red-400" },
    erledigt: { label: "Erledigt", cls: "border-[#2E2517] text-[#9C917D]" },
};
const FILTERS = ["alle", "neu", "angenommen", "abgelehnt", "erledigt", "bewerbung"];
const filterLabel = (f) => (f === "alle" ? "Alle" : f === "bewerbung" ? "Bewerbungen" : STATUS_META[f].label);
const matches = (q, f) => (f === "alle" ? true : f === "bewerbung" ? q.topic === "Bewerbung" : q.status === f);

export const InquiriesAdmin = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("alle");
    const [acceptingId, setAcceptingId] = useState(null);
    const [cancelingId, setCancelingId] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelNotify, setCancelNotify] = useState(true);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [loads, setLoads] = useState("1");
    const [notify, setNotify] = useState(true);
    const [notes, setNotes] = useState({});

    const load = useCallback(() => {
        api.get("/admin/inquiries")
            .then((r) => {
                setItems(r.data.map((q) => ({ status: "neu", ...q })));
                setNotes(Object.fromEntries(r.data.map((q) => [q.id, q.admin_note || ""])));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const toggleDone = async (q) => {
        const next = q.status === "erledigt" ? "neu" : "erledigt";
        try {
            await api.patch(`/admin/inquiries/${q.id}`, { status: next });
            toast.success(next === "erledigt" ? "Als erledigt markiert." : "Wieder geöffnet.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleDecline = async (q) => {
        const next = q.status === "abgelehnt" ? "neu" : "abgelehnt";
        try {
            await api.patch(`/admin/inquiries/${q.id}`, { status: next });
            toast.success(next === "abgelehnt" ? "Anfrage abgelehnt / aufgeschoben." : "Wieder geöffnet.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const inviteReview = async (q) => {
        try {
            await api.post(`/admin/inquiries/${q.id}/review-invite`);
            toast.success(`Bewertungs-Einladung an ${q.email} gesendet.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const saveNote = async (q) => {
        try {
            await api.patch(`/admin/inquiries/${q.id}/note`, { note: notes[q.id] || "" });
            toast.success("Notiz gespeichert.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cancel = async (q) => {
        try {
            const r = await api.post(`/admin/inquiries/${q.id}/cancel`, { reason: cancelReason || null, notify: cancelNotify });
            let msg = "Anfrage abgesagt.";
            if (r.data.freed) msg += ` ${r.data.freed} ${r.data.freed === 1 ? "Fuhre" : "Fuhren"} im Kalender freigegeben.`;
            if (cancelNotify) msg += r.data.notified ? " Der Kunde wurde per E-Mail informiert." : " E-Mail konnte nicht gesendet werden.";
            toast.success(msg);
            setCancelingId(null);
            setCancelReason("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const accept = async (q) => {
        if (!date) {
            toast.error("Bitte ein Lieferdatum wählen.");
            return;
        }
        try {
            const r = await api.post(`/admin/inquiries/${q.id}/accept`, { date, loads: Number(loads), time: time || null, notify });
            toast.success(`Eingeplant am ${new Date(date + "T12:00:00").toLocaleDateString("de-DE")} — Tag hat jetzt ${r.data.loads}/${r.data.capacity} Fuhren.${notify ? " Der Kunde wurde per E-Mail benachrichtigt." : ""}`);
            setAcceptingId(null);
            setDate("");
            setTime("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const visible = items.filter((q) => matches(q, filter));

    return (
        <div data-testid="admin-inquiries">
            <div className="mb-6 flex flex-wrap gap-2" data-testid="inquiry-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f}
                        data-testid={`inquiry-filter-${f}`}
                        onClick={() => setFilter(f)}
                        className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                            filter === f ? "border-[#C88A45] bg-[#C88A45]/10 text-[#C88A45]" : "border-[#2E2517] text-[#9C917D] hover:text-[#EDE6DA]"
                        }`}
                    >
                        {filterLabel(f)} ({items.filter((q) => matches(q, f)).length})
                    </button>
                ))}
            </div>
            {loading && <p className="text-sm text-[#9C917D]">Lade Anfragen …</p>}
            {!loading && visible.length === 0 && <p className="text-sm text-[#9C917D]">Keine Einträge in dieser Ansicht.</p>}
            <div className="space-y-4">
                {visible.map((q) => (
                    <div key={q.id} className={`rounded border bg-[#1B150E] p-6 transition-opacity ${q.status === "erledigt" || q.status === "abgelehnt" ? "border-[#2E2517] opacity-60" : "border-[#2E2517]"}`} data-testid={`inquiry-${q.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-semibold text-[#EDE6DA]">
                                {q.name} <span className="ml-2 rounded-full border border-[#2E2517] px-3 py-0.5 text-xs text-[#C88A45]">{q.topic}</span>
                            </p>
                            <span className={`rounded-full border px-3 py-0.5 text-xs font-semibold ${STATUS_META[q.status].cls}`} data-testid={`inquiry-status-${q.id}`}>
                                {STATUS_META[q.status].label}
                            </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 text-xs text-[#9C917D]">
                            <span>{new Date(q.created_at).toLocaleString("de-DE")}</span>
                            <a href={`mailto:${q.email}`} className="flex items-center gap-1 transition-colors hover:text-[#C88A45]"><Mail size={13} /> {q.email}</a>
                            {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-1 transition-colors hover:text-[#C88A45]"><Phone size={13} /> {q.phone}</a>}
                            {q.address && <span data-testid={`inquiry-address-${q.id}`}>{q.address}</span>}
                        </div>
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[#D9D2C4]">{q.message}</p>
                        <div className="mt-3 flex items-center gap-2">
                            <input
                                data-testid={`inquiry-note-${q.id}`}
                                value={notes[q.id] || ""}
                                onChange={(e) => setNotes({ ...notes, [q.id]: e.target.value })}
                                placeholder="Interne Notiz — nur im Admin sichtbar (z. B. Kunde ruft nochmal an)"
                                className="h-9 flex-1 rounded border border-dashed border-[#2E2517] bg-[#120E09] px-3 text-xs text-[#EDE6DA] placeholder:text-[#9C917D]/60"
                            />
                            <button
                                data-testid={`inquiry-note-save-${q.id}`}
                                onClick={() => saveNote(q)}
                                aria-label="Notiz speichern"
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#2E2517] text-[#9C917D] transition-colors duration-200 hover:border-[#C88A45]/50 hover:text-[#C88A45]"
                            >
                                <Save size={14} />
                            </button>
                        </div>
                        {q.status === "angenommen" && q.booked_date && (
                            <p className="mt-3 text-xs text-[#7A9B5A]" data-testid={`inquiry-booked-${q.id}`}>
                                Eingeplant am {new Date(q.booked_date + "T12:00:00").toLocaleDateString("de-DE")}{q.time ? `, ${q.time} Uhr` : ""} · {q.loads} {q.loads === 1 ? "Fuhre" : "Fuhren"}
                            </p>
                        )}
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            {q.status === "neu" && (
                                <button
                                    data-testid={`inquiry-accept-${q.id}`}
                                    onClick={() => { setAcceptingId(acceptingId === q.id ? null : q.id); setDate(""); setTime(""); setLoads("1"); }}
                                    className="flex items-center gap-2 rounded-full bg-[#C88A45] px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057]"
                                >
                                    <CalendarPlus size={14} /> Annehmen & einplanen
                                </button>
                            )}
                            <button
                                data-testid={`inquiry-decline-${q.id}`}
                                onClick={() => toggleDecline(q)}
                                className="flex items-center gap-2 rounded-full border border-[#2E2517] px-4 py-2 text-xs text-[#9C917D] transition-colors duration-200 hover:border-red-400/50 hover:text-red-400"
                            >
                                <XCircle size={14} /> {q.status === "abgelehnt" ? "Zurückholen" : "Ablehnen"}
                            </button>
                            {(q.status === "neu" || q.status === "angenommen") && (
                                <button
                                    data-testid={`inquiry-cancel-${q.id}`}
                                    onClick={() => { setCancelingId(cancelingId === q.id ? null : q.id); setCancelReason(""); setCancelNotify(true); }}
                                    className="flex items-center gap-2 rounded-full border border-red-400/40 px-4 py-2 text-xs text-red-400 transition-colors duration-200 hover:bg-red-400/10"
                                >
                                    <MailX size={14} /> Absagen
                                </button>
                            )}
                            {(q.status === "angenommen" || q.status === "erledigt") && (
                                <button
                                    data-testid={`inquiry-invite-${q.id}`}
                                    onClick={() => inviteReview(q)}
                                    disabled={q.review_invited}
                                    className="flex items-center gap-2 rounded-full border border-[#C88A45]/40 px-4 py-2 text-xs text-[#C88A45] transition-colors duration-200 hover:bg-[#C88A45]/10 disabled:opacity-50"
                                >
                                    <Send size={14} /> {q.review_invited ? "Einladung gesendet" : "Um Bewertung bitten"}
                                </button>
                            )}
                            <button
                                data-testid={`inquiry-done-${q.id}`}
                                onClick={() => toggleDone(q)}
                                className="flex items-center gap-2 rounded-full border border-[#2E2517] px-4 py-2 text-xs text-[#9C917D] transition-colors duration-200 hover:border-[#7A9B5A]/50 hover:text-[#7A9B5A]"
                            >
                                <Check size={14} /> {q.status === "erledigt" ? "Wieder öffnen" : "Erledigt"}
                            </button>
                        </div>
                        {acceptingId === q.id && (
                            <div className="mt-4 flex flex-wrap items-end gap-3 rounded border border-[#2E2517] bg-[#120E09] p-4" data-testid={`inquiry-accept-form-${q.id}`}>
                                <div>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Lieferdatum</label>
                                    <input
                                        type="date"
                                        data-testid={`inquiry-date-${q.id}`}
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        className="rounded border border-[#2E2517] bg-[#1B150E] px-3 py-2 text-sm text-[#EDE6DA] [color-scheme:dark]"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Fuhren</label>
                                    <select
                                        data-testid={`inquiry-loads-${q.id}`}
                                        value={loads}
                                        onChange={(e) => setLoads(e.target.value)}
                                        className="rounded border border-[#2E2517] bg-[#1B150E] px-3 py-2 text-sm text-[#EDE6DA]"
                                    >
                                        <option value="1">1 Fuhre</option>
                                        <option value="2">2 Fuhren</option>
                                        <option value="3">3 Fuhren</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Uhrzeit (optional)</label>
                                    <input
                                        type="time"
                                        data-testid={`inquiry-time-${q.id}`}
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        className="rounded border border-[#2E2517] bg-[#1B150E] px-3 py-2 text-sm text-[#EDE6DA] [color-scheme:dark]"
                                    />
                                </div>
                                <button
                                    data-testid={`inquiry-accept-save-${q.id}`}
                                    onClick={() => accept(q)}
                                    className="rounded-full bg-[#7A9B5A] px-5 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#8CAF68]"
                                >
                                    Einplanen
                                </button>
                                <label className="flex cursor-pointer items-center gap-2 text-xs text-[#EDE6DA]">
                                    <input
                                        type="checkbox"
                                        data-testid={`inquiry-notify-${q.id}`}
                                        checked={notify}
                                        onChange={(e) => setNotify(e.target.checked)}
                                        className="h-4 w-4 accent-[#C88A45]"
                                    />
                                    Kunde per E-Mail benachrichtigen
                                </label>
                            </div>
                        )}
                        {cancelingId === q.id && (
                            <div className="mt-4 rounded border border-red-400/30 bg-[#120E09] p-4" data-testid={`inquiry-cancel-form-${q.id}`}>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">
                                    Grund (optional — steht in der E-Mail an den Kunden)
                                </label>
                                <textarea
                                    data-testid={`inquiry-cancel-reason-${q.id}`}
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    rows={2}
                                    placeholder="z. B. Wegen eines Maschinenschadens kann ich den Termin leider nicht halten …"
                                    className="mb-3 w-full rounded border border-[#2E2517] bg-[#1B150E] px-3 py-2 text-sm text-[#EDE6DA] placeholder:text-[#9C917D]/60"
                                />
                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        data-testid={`inquiry-cancel-send-${q.id}`}
                                        onClick={() => cancel(q)}
                                        className="rounded-full bg-red-400 px-5 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-red-300"
                                    >
                                        Absage bestätigen
                                    </button>
                                    <label className="flex cursor-pointer items-center gap-2 text-xs text-[#EDE6DA]">
                                        <input
                                            type="checkbox"
                                            data-testid={`inquiry-cancel-notify-${q.id}`}
                                            checked={cancelNotify}
                                            onChange={(e) => setCancelNotify(e.target.checked)}
                                            className="h-4 w-4 accent-[#C88A45]"
                                        />
                                        Kunde per E-Mail informieren
                                    </label>
                                    {q.status === "angenommen" && (
                                        <p className="text-xs text-[#9C917D]">Der eingeplante Termin wird im Kalender wieder freigegeben.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
